import { useState, useEffect, useMemo } from 'react';
import { ArrowRight, ArrowLeft, CheckCircle2, X } from 'lucide-react';
import ModalPortal from './ModalPortal';
import { useLocation } from 'react-router-dom';

const ONBOARDING_KEY = 'simply_send_contacts_onboarding_completed_v3';

// Translation dictionary for easy localization/translation
const LABELS = {
  skipTour: 'Close',
  showLater: 'Show me later',
  back: 'Previous',
  ok: 'Next',
  done: 'Done',
  welcomeBadge: 'ONBOARDING GUIDE',
  stepIndicator: 'Step {current} of {total}',
  steps: [
    {
      stepIndex: 0,
      path: '/mailbox',
      elementId: 'nav-link-mailbox',
      badge: 'INBOUND EMAIL',
      title: 'Mailbox',
      description: 'Read, send, and manage email conversations from your assigned personal and shared mailboxes.',
      highlights: [
        'Switch between assigned mailboxes in one place',
        'Read, reply, forward, and compose messages',
        'Manage Inbox, Sent, Drafts, and Trash folders'
      ],
      gradient: 'from-sky-500/10 to-transparent dark:from-sky-950/20',
      iconColor: 'text-sky-600 dark:text-sky-400',
      iconBg: 'bg-sky-50 dark:bg-sky-950/30',
      image: '/assets/images/inbox/inbox-large.jpeg'
    },
    {
      stepIndex: 1,
      path: '/contacts',
      elementId: 'nav-link-contacts',
      parentMenu: 'contacts',
      badge: 'DIRECTORY',
      title: 'Contacts',
      description: 'Your central hub for managing all contact records under your account. Use it to view global subscriber delivery statuses, edit custom properties, etc.',
      highlights: [
        'Complete profiles with contact details',
        'Custom fields for mapping user data',
        'Global subscription status tracking (Active, Unsubscribed, Bounced)'
      ],
      gradient: 'from-cyan-500/10 to-transparent dark:from-cyan-950/20',
      iconColor: 'text-cyan-600 dark:text-cyan-400',
      iconBg: 'bg-cyan-50 dark:bg-cyan-950/30',
      image: '/assets/images/contact/contact-directory-medium.jpeg'
    },
    {
      stepIndex: 2,
      path: '/contacts/custom-fields',
      elementId: 'nav-link-contacts/custom-fields',
      parentMenu: 'contacts',
      badge: 'CUSTOM FIELDS',
      title: 'Custom Fields',
      description: 'Custom fields let you store extra information about your contacts, such as job title, company, or location. Use custom fields to personalize your campaigns and segment your contacts.',
      highlights: [
        'Add new custom fields to store additional subscriber data',
        'Map existing CSV file columns to create custom fields during import',
        'Use custom field values to personalize email content and targeting'
      ],
      gradient: 'from-blue-500/10 to-transparent dark:from-blue-950/20',
      iconColor: 'text-blue-600 dark:text-blue-400',
      iconBg: 'bg-blue-50 dark:bg-blue-950/30',
      image: '/assets/images/contact/contact-custom-fields-medium.jpeg'
    },
    {
      stepIndex: 3,
      path: '/contacts/subscription-groups',
      elementId: 'nav-link-contacts/subscription-groups',
      parentMenu: 'contacts',
      badge: 'MAILING LISTS',
      title: 'Subscription Groups (formerly Contact Lists)',
      description: 'Group subscribers for specific newsletters, campaigns, and targeted updates. Manage active subscribers, handle CSV imports, and audit consent proof data. Use subscription groups to send campaign emails to specific groups.',
      highlights: [
        'Create target mailing lists with custom fields',
        'Upload CSV files to import list subscribers directly',
        'Detailed consent logs (IP address, user-agent, method) for compliance'
      ],
      gradient: 'from-blue-500/10 to-transparent dark:from-blue-950/20',
      iconColor: 'text-blue-600 dark:text-blue-400',
      iconBg: 'bg-blue-50 dark:bg-blue-950/30',
      image: '/assets/images/contact/contact-subscription-groups-medium.jpeg'
    },
    {
      stepIndex: 4,
      path: '/contacts/segments',
      elementId: 'nav-link-contacts/segments',
      parentMenu: 'contacts',
      badge: 'DYNAMIC FILTERING',
      title: 'Segments',
      description: 'Filter your contacts dynamically using rule queries based on status, activity, or custom fields. Segments update automatically without manual database list maintenance.',
      highlights: [
        'Define rules (e.g. Custom Field: country equals "US")',
        'Automatic background segment count evaluation',
        'Instant targeting in campaigns without manually moving contacts'
      ],
      gradient: 'from-teal-500/10 to-transparent dark:from-teal-950/20',
      iconColor: 'text-teal-600 dark:text-teal-400',
      iconBg: 'bg-teal-50 dark:bg-teal-950/30',
      image: '/assets/images/contact/contact-segments-medium.jpeg'
    },
    {
      stepIndex: 5,
      path: '/workflows',
      elementId: 'nav-link-workflows',
      badge: 'AUTOMATION',
      title: 'Workflows',
      description: 'Automate contact journeys with visual drag-and-drop workflows. Trigger actions based on contact events, send emails, add delays, and branch on conditions without writing code.',
      highlights: [
        'Visual builder with drag-and-drop nodes and branches',
        'Trigger workflows on contact events or manual enrollment',
        'Automate emails, delays, conditions, and list membership changes'
      ],
      gradient: 'from-indigo-500/10 to-transparent dark:from-indigo-950/20',
      iconColor: 'text-indigo-600 dark:text-indigo-400',
      iconBg: 'bg-indigo-50 dark:bg-indigo-950/30',
      image: '/assets/images/workflow/workflow-large.jpeg'
    }
  ]
};

interface ContactsOnboardingTourProps {
  onClose?: () => void;
}

export default function ContactsOnboardingTour({ onClose }: ContactsOnboardingTourProps) {
  const location = useLocation();

  const [isVisible, setIsVisible] = useState(() => {
    const hasSeen = localStorage.getItem(ONBOARDING_KEY);
    if (hasSeen) return false;
    const paths = ['/mailbox', '/contacts', '/contacts/custom-fields', '/contacts/subscription-groups', '/contacts/segments', '/workflows'];
    return paths.includes(location.pathname);
  });

  // Track which page path the tour started on
  const [tourStartPath, setTourStartPath] = useState(() => {
    const paths = ['/mailbox', '/contacts', '/contacts/custom-fields', '/contacts/subscription-groups', '/contacts/segments', '/workflows'];
    return paths.includes(location.pathname) ? location.pathname : '/contacts';
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const [highlightRect, setHighlightRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const [postponedPathname, setPostponedPathname] = useState<string | null>(null);
  const [prevPathname, setPrevPathname] = useState(location.pathname);

  // Dynamically order steps so the active/starting page details are shown first
  const orderedSteps = useMemo(() => {
    const matchingStep = LABELS.steps.find(s => s.path === tourStartPath) || LABELS.steps[0];
    const remainingSteps = LABELS.steps.filter(s => s.path !== matchingStep.path);
    return [matchingStep, ...remainingSteps];
  }, [tourStartPath]);

  // Adjust state during render when pathname changes
  if (location.pathname !== prevPathname) {
    setPrevPathname(location.pathname);

    const hasSeen = localStorage.getItem(ONBOARDING_KEY);
    const isPostponedOnCurrentPath = postponedPathname === location.pathname;

    if (!hasSeen && !isPostponedOnCurrentPath) {
      const paths = ['/mailbox', '/contacts', '/contacts/custom-fields', '/contacts/subscription-groups', '/contacts/segments', '/workflows'];
      if (paths.includes(location.pathname)) {
        setTourStartPath(location.pathname);
        setCurrentStep(0);
        setIsVisible(true);
      }
    }
  }

  // Handle manual trigger event from child pages
  useEffect(() => {
    const handleTrigger = () => {
      setPostponedPathname(null); // Reset postponement if user manually triggers the tour
      const paths = ['/mailbox', '/contacts', '/contacts/custom-fields', '/contacts/subscription-groups', '/contacts/segments', '/workflows'];
      const startPath = paths.includes(location.pathname) ? location.pathname : '/contacts';
      setTourStartPath(startPath);
      setCurrentStep(0);
      setIsVisible(true);
    };
    window.addEventListener('trigger-contacts-tour', handleTrigger);
    return () => window.removeEventListener('trigger-contacts-tour', handleTrigger);
  }, [location.pathname]);

  const handleDismiss = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setIsVisible(false);
    setCoords(null);
    setHighlightRect(null);
    setPostponedPathname(null);
    if (onClose) {
      onClose();
    }
  };

  const handleShowLater = () => {
    setPostponedPathname(location.pathname); // Track the pathname where postponement happened to prevent immediate auto-trigger reopening
    setIsVisible(false);
    setCoords(null);
    setHighlightRect(null);
    if (onClose) {
      onClose();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentStep < orderedSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleDismiss();
    }
  };

  // Highlight the active sidebar element; keep the tour popup fixed in the center near the left menu.
  useEffect(() => {
    if (!isVisible || !orderedSteps[currentStep]) return;

    const activeStep = orderedSteps[currentStep];
    const id = activeStep.elementId;

    // Expand parent menus when the active step belongs to a collapsible submenu
    if (activeStep.parentMenu) {
      window.dispatchEvent(new CustomEvent('expand-sidebar-menu', { detail: { menu: activeStep.parentMenu } }));
    }

    const calculatePosition = () => {
      // Fallback to parent link 'nav-link-contacts' if specific link doesn't exist (e.g. collapsed sidebar)
      const element = document.getElementById(id) || document.getElementById('nav-link-contacts');

      if (element && window.innerWidth >= 1024) {
        const rect = element.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setHighlightRect({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height
          });
        } else {
          setHighlightRect(null);
        }
      } else {
        setHighlightRect(null);
      }

      // Fixed popup position: centered vertically, anchored to the right edge of the left menu
      if (window.innerWidth >= 1024) {
        const sidebarElement = document.getElementById('app-sidebar');
        const sidebarRect = sidebarElement?.getBoundingClientRect();
        const sidebarWidth = sidebarRect && sidebarRect.width > 0 ? sidebarRect.width : 256;
        setCoords({
          top: window.innerHeight / 2,
          left: sidebarWidth + 32
        });
      } else {
        setCoords(null);
      }
    };

    calculatePosition();

    // Re-calculate after parent menu expansion animation completes
    const recalcTimeout = activeStep.parentMenu ? setTimeout(calculatePosition, 350) : null;

    window.addEventListener('resize', calculatePosition);
    window.addEventListener('scroll', calculatePosition, { capture: true });

    return () => {
      if (recalcTimeout) clearTimeout(recalcTimeout);
      window.removeEventListener('resize', calculatePosition);
      window.removeEventListener('scroll', calculatePosition, { capture: true });
    };
  }, [isVisible, currentStep, orderedSteps]);

  if (!isVisible) return null;

  const stepInfo = orderedSteps[currentStep];


  // Conditional styles based on viewport presence
  const cardStyle: React.CSSProperties = coords
    ? {
      position: 'fixed',
      top: `${coords.top}px`,
      left: `${coords.left}px`,
      transform: 'translateY(-50%)',
    }
    : {};

  const cardClass = coords
    ? "relative w-[90vw] lg:w-215 max-w-[calc(100vw-360px)] max-h-[92vh] lg:max-h-none overflow-y-auto lg:overflow-y-visible bg-white/95 dark:bg-zinc-950/95 backdrop-blur-xl border border-white/30 dark:border-white/10 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.25)] flex flex-col lg:flex-row items-stretch overflow-hidden z-50 transition-all duration-300 animate-in fade-in zoom-in-95"
    : "relative w-[90vw] lg:w-215 max-w-lg lg:max-w-none max-h-[92vh] lg:max-h-none overflow-y-auto lg:overflow-y-visible bg-white/95 dark:bg-zinc-950/95 backdrop-blur-xl border border-white/30 dark:border-white/10 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.25)] flex flex-col lg:flex-row items-stretch overflow-hidden z-50 transition-all duration-300 animate-in fade-in zoom-in-95";

  return (
    <ModalPortal>
      {/* Spotlight/Backdrop Overlay */}
      {highlightRect ? (
        <div
          className="fixed inset-0 z-90 pointer-events-auto bg-transparent"
        >
          <div
            className="absolute bg-transparent rounded-xl border-2 border-cyan-500/85 shadow-[0_0_0_9999px_rgba(255,255,255,0.7)] dark:shadow-[0_0_0_9999px_rgba(0,0,0,0.75)] pointer-events-none transition-all duration-300 animate-in fade-in"
            style={{
              top: `${highlightRect.top - 4}px`,
              left: `${highlightRect.left - 4}px`,
              width: `${highlightRect.width + 8}px`,
              height: `${highlightRect.height + 8}px`,
            }}
          />
        </div>
      ) : (
        <div
          className="fixed inset-0 z-90 pointer-events-auto bg-white/70 dark:bg-black/75 transition-opacity duration-300 animate-in fade-in"
        />
      )}

      {/* Tour Card container */}
      <div className={`pointer-events-none z-100 ${coords ? 'fixed inset-0 flex items-center justify-center' : 'fixed inset-0 flex items-center justify-center p-4'}`}>
        <div
          style={cardStyle}
          className={`${cardClass} pointer-events-auto`}
        >
          {/* Left side: Graphic Illustration (Portrait, full top-to-bottom on left) */}
          <div className="w-full lg:w-85 h-95 lg:h-auto bg-zinc-50 dark:bg-zinc-900/40 border-b lg:border-b-0 lg:border-r border-zinc-200/30 dark:border-zinc-800/50 relative shrink-0 flex items-center justify-center">
            <img
              src={stepInfo.image}
              alt={stepInfo.title}
              className="w-full h-full object-cover object-top hover:scale-[1.02] transition-transform duration-500"
            />
          </div>

          {/* Right side: Header, Description & Footer */}
          <div className="flex-1 flex flex-col justify-between relative rounded-b-3xl lg:rounded-b-none lg:rounded-r-3xl overflow-hidden">
            {/* Top Panel - Heading with step gradient */}
            <div className={`p-6 pb-4 border-b border-zinc-200/20 dark:border-zinc-800/30 bg-linear-to-r ${stepInfo.gradient} transition-all duration-500 flex items-center justify-between`}>
              <div className="flex items-center gap-2.5">
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-cyan-600/10 text-cyan-700 dark:bg-cyan-400/10 dark:text-cyan-400 uppercase tracking-wider">
                  {LABELS.welcomeBadge}
                </span>
                <span className="text-[10px] font-bold text-zinc-500 dark:text-zinc-400">
                  {LABELS.stepIndicator
                    .replace('{current}', String(currentStep + 1))
                    .replace('{total}', String(orderedSteps.length))}
                </span>
              </div>

              <div className="flex items-center gap-3.5">
                {/* Show me later Button */}
                <button
                  onClick={handleShowLater}
                  className="px-3 py-1 bg-zinc-900 hover:bg-zinc-800 text-zinc-100 border border-zinc-950 dark:bg-white dark:hover:bg-zinc-100 dark:text-zinc-900 dark:border-white rounded-full text-[10px] font-bold transition-all hover:scale-105 active:scale-95"
                >
                  {LABELS.showLater}
                </button>

                {/* Close Tour Icon Button */}
                <button
                  onClick={handleDismiss}
                  className="p-1 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 rounded-full transition-colors flex items-center justify-center shrink-0"
                  aria-label="Close tour"
                  title="Close Tour"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Description & Key Capabilities */}
            <div className="p-6 lg:p-8 flex-1 flex flex-col justify-center space-y-5">
              <p className="text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed font-medium">
                {stepInfo.description}
              </p>

              <div className="space-y-2.5">
                <span className="text-[10px] font-extrabold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest block">Key Capabilities</span>
                <ul className="space-y-2.5">
                  {stepInfo.highlights.map((highlight, index) => (
                    <li key={index} className="flex items-start gap-2.5 text-xs text-zinc-700 dark:text-zinc-400 font-medium">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Bottom Panel - Controls */}
            <div className="px-6 py-4 bg-zinc-50/20 dark:bg-zinc-950/10 border-t border-zinc-200/20 dark:border-zinc-800/40 flex items-center justify-between rounded-b-3xl lg:rounded-bl-none">
              {/* Step Dots indicator */}
              <div className="flex items-center gap-1.5">
                {orderedSteps.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentStep(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${i === currentStep
                      ? 'w-5 bg-zinc-900 dark:bg-white'
                      : 'w-1.5 bg-zinc-300 dark:bg-zinc-700 hover:bg-zinc-400'
                      }`}
                    aria-label={`Go to step ${i + 1}`}
                  />
                ))}
              </div>

              {/* Navigation Buttons */}
              <div className="flex items-center gap-3">

                {currentStep > 0 && (
                  <button
                    onClick={handleBack}
                    className="px-3.5 py-2 text-xs font-bold text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors flex items-center gap-1"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    {LABELS.back}
                  </button>
                )}

                <button
                  onClick={handleNext}
                  className="inline-flex items-center gap-1.5 px-6 py-2 bg-zinc-900 hover:bg-zinc-800 text-zinc-100 dark:bg-white dark:hover:bg-zinc-100 dark:text-zinc-900 font-extrabold rounded-full text-xs transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm"
                >
                  <span>{currentStep === orderedSteps.length - 1 ? LABELS.done : LABELS.ok}</span>
                  {currentStep < orderedSteps.length - 1 && <ArrowRight className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
